import { useEffect, useState } from 'react'
import { Form } from 'factorial-form'

import { PayrollConceptSelectFieldFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { PayrollConceptSelect } from 'modules/compensations/components/PayrollConceptSelectField'
import { useCategorizedPayrollConcepts } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import { translate } from 'modules/contracts/lib/translate'

interface BaseSalaryTypeProps {
  form: Form
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  translations: { [key: string]: string }
}

const BaseSalaryType = ({ form, payrollConcepts, translations }: BaseSalaryTypeProps) => {
  const categorizedPayrollConcepts = useCategorizedPayrollConcepts(payrollConcepts, {
    excludeBaseSalary: false,
  }).sort((a, b) => a.label.localeCompare(b.label))
  const hasField = form.has('de_base_salary_type_id')
  const deBaseSalaryTypeField = hasField ? form.get('de_base_salary_type_id') : null

  const normalizeValue = (value: string | number | null | undefined): number | null => {
    if (value == null || value === '') return null
    return typeof value === 'number' ? value : Number(value)
  }

  const [selectedValue, setSelectedValue] = useState<number | null>(
    normalizeValue(deBaseSalaryTypeField?.value)
  )

  useEffect(() => {
    if (!deBaseSalaryTypeField) return

    const formValue = normalizeValue(deBaseSalaryTypeField.value)
    if (formValue !== selectedValue) {
      setSelectedValue(formValue)
    }
  }, [deBaseSalaryTypeField, deBaseSalaryTypeField?.value, selectedValue])

  if (!deBaseSalaryTypeField) {
    return null
  }

  const handleChange = (value: unknown) => {
    const selected = Array.isArray(value) ? value[0] : value
    const normalizedId = normalizeValue(selected)
    setSelectedValue(normalizedId)
    deBaseSalaryTypeField.set(normalizedId)
  }

  return (
    <PayrollConceptSelect
      payrollConcepts={categorizedPayrollConcepts}
      value={selectedValue !== null ? [selectedValue] : []}
      onChange={handleChange}
      label={translate(translations, 'de_base_salary_type_id')}
      mandatory={false}
    />
  )
}

export default observer(BaseSalaryType)
